// tooltip.js — schlichte M3-Tooltips, ein gemeinsames Element am body, per Delegation.
//   Quellen: [data-tip="Text"] (Text wird per aria-describedby vorgelesen)
//            .vc-icon-btn[aria-label] und .vc-fab[aria-label] ohne sichtbaren Text (nur sichtbar — der Name steht schon im aria-label)
//   Maus: nach 500 ms Verweilen. Tastatur: sofort beim Fokus. Touch: langes Drücken (≥ 500 ms).
//   Esc, Scrollen, Verlassen und Klick schließen. Doppelt einbinden ist harmlos.
const DELAY = 500;
const SEL = '[data-tip], .vc-icon-btn[aria-label], .vc-fab[aria-label]';
let tip = null, anchor = null, timer = 0, installed = false, keyboard = false;

/** Text für ein Element — null, wenn es keinen Tooltip braucht. */
export function tipText(el) {
  if (!el || el.matches('[data-no-tip]')) return null;
  if (el.dataset.tip) return el.dataset.tip;
  const label = el.getAttribute('aria-label');
  if (!label) return null;
  // Nur bei reinen Symbol-Knöpfen: steht sichtbarer Text daneben, wäre der Tooltip doppelt.
  const visible = [...el.childNodes].some((n) => (n.nodeType === 3 ? n.textContent.trim() : n.nodeType === 1 && !n.matches('.vc-icon, [aria-hidden="true"], .vc-ripple') && n.textContent.trim()));
  return visible ? null : label;
}

/** Position unter dem Anker, sonst darüber; seitlich im Fenster gehalten. */
export function placeTip(r, w, h, vw, vh, gap = 8) {
  const below = r.bottom + gap + h <= vh - 8;
  const top = below ? r.bottom + gap : Math.max(8, r.top - gap - h);
  const left = Math.min(Math.max(8, r.left + r.width / 2 - w / 2), vw - w - 8);
  return { top, left, below };
}

function el() {
  if (tip && tip.isConnected) return tip;
  tip = document.createElement('div');
  tip.className = 'vc-tooltip';
  tip.id = 'vc-tooltip';
  tip.setAttribute('role', 'tooltip');
  tip.hidden = true;
  document.body.appendChild(tip);
  return tip;
}

function show(a) {
  const text = tipText(a);
  if (!text) return;
  hide();
  const t = el();
  t.textContent = text;
  t.hidden = false;
  const p = placeTip(a.getBoundingClientRect(), t.offsetWidth, t.offsetHeight, document.documentElement.clientWidth, innerHeight);
  t.style.top = p.top + 'px'; t.style.left = p.left + 'px';
  t.dataset.side = p.below ? 'below' : 'above';
  anchor = a;
  if (a.dataset.tip) {
    const ids = (a.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    if (!ids.includes(t.id)) a.setAttribute('aria-describedby', [...ids, t.id].join(' '));
  } else t.setAttribute('aria-hidden', 'true');
}

export function hide() {
  clearTimeout(timer);
  if (!tip) return;
  tip.hidden = true;
  tip.removeAttribute('aria-hidden');
  if (anchor && anchor.dataset.tip) {
    const ids = (anchor.getAttribute('aria-describedby') || '').split(/\s+/).filter((x) => x && x !== tip.id);
    if (ids.length) anchor.setAttribute('aria-describedby', ids.join(' ')); else anchor.removeAttribute('aria-describedby');
  }
  anchor = null;
}

export function installTooltips() {
  if (installed) return;
  installed = true;
  const later = (a) => { clearTimeout(timer); timer = setTimeout(() => show(a), DELAY); };
  document.addEventListener('keydown', (e) => { keyboard = true; if (e.key === 'Escape' && anchor) hide(); }, true);
  document.addEventListener('pointerdown', (e) => {
    keyboard = false;
    hide();
    const a = e.target instanceof Element && e.target.closest(SEL);
    if (a && e.pointerType === 'touch') later(a); // langes Drücken
  }, true);
  document.addEventListener('pointerup', () => clearTimeout(timer), true);
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType === 'touch') return;
    const a = e.target instanceof Element && e.target.closest(SEL);
    if (a && a !== anchor && !(e.relatedTarget instanceof Node && a.contains(e.relatedTarget))) later(a);
  });
  document.addEventListener('pointerout', (e) => {
    const a = e.target instanceof Element && e.target.closest(SEL);
    if (a && !(e.relatedTarget instanceof Node && a.contains(e.relatedTarget))) { clearTimeout(timer); if (a === anchor) hide(); }
  });
  document.addEventListener('focusin', (e) => { const a = e.target instanceof Element && e.target.closest(SEL); if (a && keyboard && a === e.target) show(a); });
  document.addEventListener('focusout', (e) => { if (e.target === anchor) hide(); });
  addEventListener('scroll', () => anchor && hide(), true);
  addEventListener('resize', () => anchor && hide());
}
